import type { BulkActionOperationType } from '@web-config-app/core';
import type {
  BulkActionControllerStatus,
  BulkActionItemStatus,
  BulkActionSession,
} from './bulk-action-reporting.types';

export type BulkActionProgressStatus = 'loading' | 'success' | 'error';

export type BulkActionBannerStatus = 'success' | 'warning' | 'error';

export interface BulkActionProgressViewModel {
  status: BulkActionProgressStatus;
  mode: 'determinate' | 'indeterminate';
  totalCount: number;
  pendingCount: number;
  processedCount: number;
  counts: {
    succeeded: number;
    skipped: number;
    error: number;
    unprocessed: number;
  };
}

export interface BulkActionTableRowViewModel {
  id: string;
  status: BulkActionItemStatus;
  label: string;
  text: string;
  secondaryText?: string;
}

type BulkActionBannerDerivedStatus =
  | 'completed'
  | 'completedWithSkips'
  | 'completedWithErrors'
  | 'failed';

export interface BulkActionBannerViewModel {
  status: BulkActionBannerStatus;
  titleMessageId: string;
  messageMessageId?: string;
  messageOperationMessageId?: string;
  messageOperationPastTenseMessageId?: string;
}

export type BulkActionModalTitleStatus =
  | 'active'
  | 'completed'
  | 'completedWithErrors'
  | 'failed';

export interface BulkActionModalTitleViewModel {
  status: BulkActionModalTitleStatus;
  titleMessageId: string;
  operationMessageId: string;
}

const defaultTitleMessageIdByStatus: Record<
  BulkActionModalTitleStatus,
  string
> = {
  active: 'BULK_ACTION_REPORTING_MODAL_TITLE',
  completed: 'BULK_ACTION_REPORTING_MODAL_TITLE',
  completedWithErrors:
    'BULK_ACTION_REPORTING_MODAL_TITLE_COMPLETED_WITH_ERRORS',
  failed: 'BULK_ACTION_REPORT_ERROR_FALLBACK',
};

const bulkActionModalTitleMessageIdByType: Record<
  BulkActionOperationType,
  Record<BulkActionModalTitleStatus, string>
> = {
  import: defaultTitleMessageIdByStatus,
  export: defaultTitleMessageIdByStatus,
  clone: defaultTitleMessageIdByStatus,
  publish: defaultTitleMessageIdByStatus,
  archive: defaultTitleMessageIdByStatus,
  delete: defaultTitleMessageIdByStatus,
  copyToEnvironment: {
    active: 'BULK_ACTION_REPORTING_MODAL_TITLE_COPYING_TO_ENV',
    completed: 'BULK_ACTION_REPORTING_MODAL_TITLE_DONE_COPYING_TO_ENV',
    completedWithErrors:
      'BULK_ACTION_REPORTING_MODAL_TITLE_DONE_COPYING_TO_ENV',
    failed: 'BULK_ACTION_REPORT_ERROR_FALLBACK',
  },
};

const bulkActionModalTitleOperationMessageIdByType: Record<
  BulkActionOperationType,
  Record<BulkActionModalTitleStatus, string>
> = {
  import: {
    active: 'BULK_ACTION_REPORTING_PROGRESS_ACTION_IMPORTING',
    completed: 'BULK_ACTION_REPORT_STATUS_IMPORTED',
    completedWithErrors: 'BULK_ACTION_REPORT_STATUS_IMPORTED',
    failed: 'BULK_ACTION_REPORT_OPERATION_TYPE_IMPORT',
  },
  export: {
    active: 'BULK_ACTION_REPORTING_PROGRESS_ACTION_EXPORTING',
    completed: 'BULK_ACTION_REPORT_STATUS_EXPORTED',
    completedWithErrors: 'BULK_ACTION_REPORT_STATUS_EXPORTED',
    failed: 'BULK_ACTION_REPORT_OPERATION_TYPE_EXPORT',
  },
  copyToEnvironment: {
    active: 'BULK_ACTION_REPORTING_PROGRESS_ACTION_COPYING',
    completed: 'BULK_ACTION_REPORT_STATUS_COPIED',
    completedWithErrors: 'BULK_ACTION_REPORT_STATUS_COPIED',
    failed: 'BULK_ACTION_REPORT_OPERATION_TYPE_COPY',
  },
  clone: {
    active: 'BULK_ACTION_REPORTING_PROGRESS_ACTION_DUPLICATING',
    completed: 'BULK_ACTION_REPORTING_MODAL_TITLE_VERB_DUPLICATED',
    completedWithErrors: 'BULK_ACTION_REPORTING_MODAL_TITLE_VERB_DUPLICATED',
    failed: 'BULK_ACTION_REPORT_OPERATION_TYPE_DUPLICATE',
  },
  publish: {
    active: 'BULK_ACTION_REPORTING_PROGRESS_ACTION_PUBLISHING',
    completed: 'BULK_ACTION_REPORT_STATUS_PUBLISHED',
    completedWithErrors: 'BULK_ACTION_REPORT_STATUS_PUBLISHED',
    failed: 'BULK_ACTION_REPORT_OPERATION_TYPE_PUBLISH',
  },
  archive: {
    active: 'BULK_ACTION_REPORTING_PROGRESS_ACTION_ARCHIVING',
    completed: 'BULK_ACTION_REPORTING_MODAL_TITLE_VERB_ARCHIVED',
    completedWithErrors: 'BULK_ACTION_REPORTING_MODAL_TITLE_VERB_ARCHIVED',
    failed: 'BULK_ACTION_REPORT_OPERATION_TYPE_ARCHIVE',
  },
  delete: {
    active: 'BULK_ACTION_REPORTING_PROGRESS_ACTION_DELETING',
    completed: 'BULK_ACTION_REPORTING_MODAL_TITLE_VERB_DELETED',
    completedWithErrors: 'BULK_ACTION_REPORTING_MODAL_TITLE_VERB_DELETED',
    failed: 'BULK_ACTION_REPORT_OPERATION_TYPE_DELETE',
  },
};

const getBulkActionModalTitleStatus = (
  session: BulkActionSession,
): BulkActionModalTitleStatus => {
  if (session.status === 'failed') {
    return 'failed';
  }

  if (session.status === 'completed' && session.stats.failed > 0) {
    return 'completedWithErrors';
  }

  if (session.status === 'completed') {
    return 'completed';
  }

  return 'active';
};

export const mapControllerStatusToProgressStatus = ({
  controllerStatus,
  failedCount,
}: {
  controllerStatus: BulkActionControllerStatus;
  failedCount: number;
}): BulkActionProgressStatus => {
  if (controllerStatus === 'failed') {
    return 'error';
  }
  if (controllerStatus === 'completed') {
    return failedCount > 0 ? 'error' : 'success';
  }
  return 'loading';
};

export const mapBulkActionSessionToProgressViewModel = (
  session: BulkActionSession,
): BulkActionProgressViewModel => {
  const totalCount = Math.max(session.stats.total, 0);
  const pendingCount = Math.max(session.stats.pending, 0);
  const processedCount = Math.max(totalCount - pendingCount, 0);
  const status = mapControllerStatusToProgressStatus({
    controllerStatus: session.status,
    failedCount: session.stats.failed,
  });

  return {
    status,
    mode: totalCount === 0 ? 'indeterminate' : 'determinate',
    totalCount,
    pendingCount,
    processedCount,
    counts: {
      succeeded: session.stats.succeeded,
      skipped: session.stats.skipped,
      error: session.stats.failed,
      unprocessed: pendingCount,
    },
  };
};

const getBulkActionBannerDerivedStatus = (
  session: BulkActionSession,
): BulkActionBannerDerivedStatus | undefined => {
  if (session.status === 'failed') {
    return 'failed';
  }

  if (session.status === 'completed' && session.stats.failed > 0) {
    return 'completedWithErrors';
  }

  if (session.status === 'completed' && session.stats.skipped > 0) {
    return 'completedWithSkips';
  }

  if (session.status === 'completed') {
    return 'completed';
  }

  return undefined;
};

const bulkActionBannerStatusByOperationAndDerivedStatus: Record<
  BulkActionOperationType,
  Partial<Record<BulkActionBannerDerivedStatus, BulkActionBannerStatus>>
> = {
  import: {
    completed: 'success',
    completedWithSkips: 'success',
    completedWithErrors: 'warning',
    failed: 'error',
  },
  export: {
    completed: 'success',
    completedWithSkips: 'success',
    completedWithErrors: 'warning',
    failed: 'error',
  },
  copyToEnvironment: {
    completed: 'success',
    completedWithSkips: 'success',
    completedWithErrors: 'warning',
    failed: 'error',
  },
  clone: {
    completed: 'success',
    completedWithSkips: 'success',
    completedWithErrors: 'warning',
    failed: 'error',
  },
  publish: {
    completed: 'success',
    completedWithSkips: 'success',
    completedWithErrors: 'warning',
    failed: 'error',
  },
  archive: {
    completed: 'success',
    completedWithSkips: 'success',
    completedWithErrors: 'warning',
    failed: 'error',
  },
  delete: {
    completed: 'success',
    completedWithSkips: 'success',
    completedWithErrors: 'warning',
    failed: 'error',
  },
};

const bulkActionBannerTitleMessageIdByOperationAndDerivedStatus: Record<
  BulkActionOperationType,
  Partial<Record<BulkActionBannerDerivedStatus, string>>
> = {
  import: {
    completed: 'BULK_ACTION_BANNER_IMPORT_COMPLETED_TITLE',
    completedWithSkips: 'BULK_ACTION_BANNER_IMPORT_COMPLETED_WITH_SKIPS_TITLE',
    completedWithErrors:
      'BULK_ACTION_BANNER_IMPORT_COMPLETED_WITH_ERRORS_TITLE',
    failed: 'BULK_ACTION_BANNER_IMPORT_FAILED_TITLE',
  },
  export: {
    completed: 'BULK_ACTION_BANNER_EXPORT_COMPLETED_TITLE',
    completedWithSkips: 'BULK_ACTION_BANNER_EXPORT_COMPLETED_WITH_SKIPS_TITLE',
    completedWithErrors:
      'BULK_ACTION_BANNER_EXPORT_COMPLETED_WITH_ERRORS_TITLE',
    failed: 'BULK_ACTION_BANNER_EXPORT_FAILED_TITLE',
  },
  copyToEnvironment: {
    completed: 'BULK_ACTION_BANNER_COPY_COMPLETED_TITLE',
    completedWithSkips: 'BULK_ACTION_BANNER_COPY_COMPLETED_WITH_SKIPS_TITLE',
    completedWithErrors: 'BULK_ACTION_BANNER_COPY_COMPLETED_WITH_ERRORS_TITLE',
    failed: 'BULK_ACTION_BANNER_COPY_FAILED_TITLE',
  },
  clone: {
    completed: 'BULK_ACTION_BANNER_CLONE_COMPLETED_TITLE',
    completedWithSkips: 'BULK_ACTION_BANNER_CLONE_COMPLETED_TITLE',
    completedWithErrors: 'BULK_ACTION_BANNER_CLONE_COMPLETED_WITH_ERRORS_TITLE',
    failed: 'BULK_ACTION_BANNER_CLONE_FAILED_TITLE',
  },
  publish: {
    completed: 'BULK_ACTION_BANNER_PUBLISH_COMPLETED_TITLE',
    completedWithSkips: 'BULK_ACTION_BANNER_PUBLISH_COMPLETED_WITH_SKIPS_TITLE',
    completedWithErrors:
      'BULK_ACTION_BANNER_PUBLISH_COMPLETED_WITH_ERRORS_TITLE',
    failed: 'BULK_ACTION_BANNER_PUBLISH_FAILED_TITLE',
  },
  archive: {
    completed: 'BULK_ACTION_BANNER_ARCHIVE_COMPLETED_TITLE',
    completedWithSkips: 'BULK_ACTION_BANNER_ARCHIVE_COMPLETED_WITH_SKIPS_TITLE',
    completedWithErrors:
      'BULK_ACTION_BANNER_ARCHIVE_COMPLETED_WITH_ERRORS_TITLE',
    failed: 'BULK_ACTION_BANNER_ARCHIVE_FAILED_TITLE',
  },
  delete: {
    completed: 'BULK_ACTION_BANNER_DELETE_COMPLETED_TITLE',
    completedWithSkips: 'BULK_ACTION_BANNER_DELETE_COMPLETED_WITH_SKIPS_TITLE',
    completedWithErrors:
      'BULK_ACTION_BANNER_DELETE_COMPLETED_WITH_ERRORS_TITLE',
    failed: 'BULK_ACTION_BANNER_DELETE_FAILED_TITLE',
  },
};

const bulkActionBannerMessageMessageIdByOperationAndDerivedStatus: Record<
  BulkActionOperationType,
  Partial<Record<BulkActionBannerDerivedStatus, string>>
> = {
  import: {
    completedWithSkips:
      'BULK_ACTION_BANNER_IMPORT_COMPLETED_WITH_SKIPS_MESSAGE',
    completedWithErrors:
      'BULK_ACTION_BANNER_IMPORT_COMPLETED_WITH_ERRORS_MESSAGE',
    failed: 'BULK_ACTION_BANNER_FAILED_MESSAGE',
  },
  export: {
    completedWithSkips:
      'BULK_ACTION_BANNER_EXPORT_COMPLETED_WITH_SKIPS_MESSAGE',
    completedWithErrors:
      'BULK_ACTION_BANNER_EXPORT_COMPLETED_WITH_ERRORS_MESSAGE',
    failed: 'BULK_ACTION_BANNER_FAILED_MESSAGE',
  },
  copyToEnvironment: {
    completedWithSkips: 'BULK_PROMOTE_MESSAGE_SKIPPED_ENTITIES',
    completedWithErrors:
      'BULK_ACTION_BANNER_COPY_COMPLETED_WITH_ERRORS_MESSAGE',
    failed: 'BULK_ACTION_BANNER_FAILED_MESSAGE',
  },
  clone: {
    completedWithErrors:
      'BULK_ACTION_BANNER_CLONE_COMPLETED_WITH_ERRORS_MESSAGE',
    failed: 'BULK_ACTION_BANNER_FAILED_MESSAGE',
  },
  publish: {
    completedWithErrors:
      'BULK_ACTION_BANNER_PUBLISH_COMPLETED_WITH_ERRORS_MESSAGE',
    failed: 'BULK_ACTION_BANNER_FAILED_MESSAGE',
  },
  archive: {
    completedWithErrors:
      'BULK_ACTION_BANNER_ARCHIVE_COMPLETED_WITH_ERRORS_MESSAGE',
    failed: 'BULK_ACTION_BANNER_FAILED_MESSAGE',
  },
  delete: {
    completedWithErrors:
      'BULK_ACTION_BANNER_DELETE_COMPLETED_WITH_ERRORS_MESSAGE',
    failed: 'BULK_ACTION_BANNER_FAILED_MESSAGE',
  },
};

const bulkActionBannerMessageOperationMessageIdByOperationType: Record<
  BulkActionOperationType,
  string
> = {
  import: 'BULK_ACTION_REPORT_OPERATION_TYPE_IMPORT',
  export: 'BULK_ACTION_REPORT_OPERATION_TYPE_EXPORT',
  copyToEnvironment: 'BULK_ACTION_REPORT_OPERATION_TYPE_COPY',
  clone: 'BULK_ACTION_REPORT_OPERATION_NOUN_DUPLICATION',
  publish: 'BULK_ACTION_REPORT_OPERATION_NOUN_PUBLISH',
  archive: 'BULK_ACTION_REPORT_OPERATION_NOUN_ARCHIVE',
  delete: 'BULK_ACTION_REPORT_OPERATION_NOUN_DELETE',
};

const bulkActionBannerMessageOperationPastTenseMessageIdByOperationType: Record<
  BulkActionOperationType,
  string
> = {
  import: 'BULK_ACTION_BANNER_FAILED_OPERATION_PAST_TENSE_IMPORTED',
  export: 'BULK_ACTION_BANNER_FAILED_OPERATION_PAST_TENSE_EXPORTED',
  copyToEnvironment: 'BULK_ACTION_BANNER_FAILED_OPERATION_PAST_TENSE_COPIED',
  clone: 'BULK_ACTION_BANNER_FAILED_OPERATION_PAST_TENSE_DUPLICATED',
  publish: 'BULK_ACTION_BANNER_FAILED_OPERATION_PAST_TENSE_PUBLISHED',
  archive: 'BULK_ACTION_BANNER_FAILED_OPERATION_PAST_TENSE_ARCHIVED',
  delete: 'BULK_ACTION_BANNER_FAILED_OPERATION_PAST_TENSE_DELETED',
};

export const mapBulkActionSessionToBannerViewModel = (
  session: BulkActionSession,
): BulkActionBannerViewModel | undefined => {
  const derivedStatus = getBulkActionBannerDerivedStatus(session);

  if (!derivedStatus) {
    return undefined;
  }

  const { operationType } = session;

  const bannerStatus =
    bulkActionBannerStatusByOperationAndDerivedStatus[operationType][
      derivedStatus
    ];

  const titleMessageId =
    bulkActionBannerTitleMessageIdByOperationAndDerivedStatus[operationType][
      derivedStatus
    ];

  if (!bannerStatus || !titleMessageId) {
    return undefined;
  }

  const messageMessageId =
    bulkActionBannerMessageMessageIdByOperationAndDerivedStatus[operationType][
      derivedStatus
    ];

  return {
    status: bannerStatus,
    titleMessageId,
    messageMessageId,
    messageOperationMessageId:
      derivedStatus === 'failed'
        ? bulkActionBannerMessageOperationMessageIdByOperationType[
            operationType
          ]
        : undefined,
    messageOperationPastTenseMessageId:
      derivedStatus === 'failed'
        ? bulkActionBannerMessageOperationPastTenseMessageIdByOperationType[
            operationType
          ]
        : undefined,
  };
};

export const mapBulkActionSessionToTitleViewModel = (
  session: BulkActionSession,
): BulkActionModalTitleViewModel => {
  const status = getBulkActionModalTitleStatus(session);

  return {
    status,
    titleMessageId:
      bulkActionModalTitleMessageIdByType[session.operationType][status],
    operationMessageId:
      bulkActionModalTitleOperationMessageIdByType[session.operationType][
        status
      ],
  };
};
